<style scoped lang="scss">
  label {
    @apply text-gray-900 font-semibold text-12 mb-2;
  }
  .select {
    :deep(.vs__dropdown-toggle) {
      @apply min-h-[41px] w-full px-4 border text-14 border-gray-200 rounded-[8px] placeholder-gray-900 placeholder-opacity-50 focus:border-secondary-500 transition-all;

      .vs__selected {
        @apply relative p-0;
      }

      .vs__selected-options {
        @apply px-0;
      }
      input {
        @apply flex items-center p-0;
        &::placeholder {
          @apply text-gray-900 text-opacity-50;
        }
      }
    }

    &:hover {
      :deep(.vs__dropdown-toggle) {
        @apply border-gray-500;
      }
    }

    &.vs--open {
      :deep(.vs__dropdown-toggle) {
        @apply border-secondary-300;
      }
      :deep(.vs__dropdown-menu) {
        @apply border-secondary-300 shadow-none;
      }
    }

    &.vs--disabled {
      :deep(.vs__dropdown-toggle) {
        @apply bg-gray-50 border-gray-300;

        .vs__search {
          @apply bg-gray-50;
        }
      }
    }

    &.failed {
      :deep(.vs__dropdown-toggle) {
        @apply border-red;
      }
    }
  }

  :deep(.vs--multiple .vs__selected) {
    background: theme("colors.secondary.300");
    padding: 2px 8px !important;
    border-radius: 999px;
    color: theme("colors.secondary.500");
    border-color: theme("colors.secondary.300");
    font-size: 0.75rem;
    line-height: 0.875rem;
  }
  :deep(.vs--multiple .vs__deselect) {
    fill: theme("colors.secondary.500");
    margin-left: 6px;
  }
</style>

<style lang="scss">
  .vs__dropdown-menu {
    @apply py-0 !max-h-[140px] !border-secondary-300 !border-solid !shadow-none !p-0;
    z-index: 9999;

    li {
      @apply text-14 px-4 py-2;
      &.vs__dropdown-option--highlight {
        @apply bg-secondary-300 text-black;
      }
      &.vs__dropdown-option--selected {
        @apply bg-secondary-300;
      }
    }
  }
</style>

<template>
  <div :class="[className, { 'disabled cursor-not-allowed': disabled, 'is-focussed': isFocussed }]">
    <label v-if="label" :for="`id_${name}`" :class="[labelClass]" class="mb-[4px] flex flex-row gap-1">
      <span v-html="label"></span>
      <span v-if="(rules && typeof rules === 'string' && rules.includes('required') && !rules.includes('required_if')) || hasRequired || fakeRequired" class="text-red">*</span>
    </label>
    <div class="flex flex-row gap-[8px]">
      <div class="group relative flex items-center flex-1 min-w-0" :class="{ hovering }" @mouseover="hovering = true" @mouseleave="hovering = false">
        <span
          ref="prefixEl"
          v-if="$slots.prefix"
          class="prefix z-10 absolute border-t border-b border-l left-0 rounded-l-[8px] px-3 h-full flex items-center bg-gray-300"
          :class="{ '!bg-red !border-red !text-white': shouldShowError }"
        >
          <slot name="prefix" v-bind="{ disabled }"></slot>
        </span>
        <span ref="selectContainer" @click.stop="toggleDropdown" :class="'w-full'">
          <v-select
            @focus="handleFocus"
            @blur="handleBlur"
            @update:modelValue="handleInput"
            @search="
              (query, loading) => {
                if (multiple) {
                  dropdownShouldOpen = true;
                }
                $emit('search', query, loading);
              }
            "
            :disabled="disabled"
            :class="['select w-full focus:outline-none flex items-center transition ease-in-out', { failed: shouldShowError }]"
            :id="`id_${name}`"
            :placeholder="placeholder"
            :clearable="clearable"
            :aria-describedby="`description_${name}`"
            :options="options"
            :create-option="createOption"
            :reduce="reduce"
            :searchable="searchable"
            :taggable="taggable"
            :multiple="multiple"
            :selectable="enabledOptions"
            v-model="localValue"
            :dropdown-should-open="() => dropdownShouldOpen"
            :calculate-position="withPopper"
            autoscroll
            append-to-body
          >
            <template #selected-option="option">
              <div class="flex items-center gap-[8px]">
                <user-image v-if="option.user" :user="option.user" class="w-[20px] h-[20px] rounded-full relative"></user-image>
                <span v-html="option.label"></span>
              </div>
            </template>

            <template v-slot:option="option">
              <div class="flex items-center gap-[8px]" :style="{ paddingLeft: `${option.indent ? option.indent * 12 : 0}px` }">
                <user-image v-if="option.user" :user="option.user" class="w-[20px] h-[20px] rounded-full relative"></user-image>
                <component
                  v-if="option.icon"
                  :is="`${option.icon
                    .split('-')
                    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                    .join('')}Icon`"
                  class="h-3 fill-secondary-500 px-4"
                ></component>
                <span v-html="option.label"></span>
              </div>
            </template>
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                Geen resultaten gevonden voor
                <em>{{ search }}</em>
                .
              </template>
              <em v-else style="opacity: 0.5">Start met typen om te zoeken...</em>
            </template>
            <template #list-footer>
              <li v-if="canAddNew" @click="addNew" class="hover:bg-secondary-300 cursor-pointer hover:text-secondary-500 transition-colors flex items-center flex-row gap-[4px]">
                <IconPlus class="h-[12px] fill-current"></IconPlus>
                Nieuw contact toevoegen...
              </li>
            </template>
          </v-select>
        </span>
        <span
          ref="suffixEl"
          v-if="$slots.suffix"
          class="suffix z-10 absolute border-t border-b border-r right-0 rounded-r-[8px] px-3 h-full flex items-center bg-gray-300"
          :class="{ '!bg-red !border-red !text-white': shouldShowError }"
        >
          <slot name="suffix"></slot>
        </span>
      </div>
      <slot name="actions-right"></slot>
    </div>
    <span v-if="shouldShowError" class="text-red text-12 block mt-2">{{ errorMessage }}</span>
  </div>
</template>

<script>
  import { useField } from "vee-validate";
  import { computed, ref, watch, onMounted, onUpdated, onUnmounted, getCurrentInstance } from "vue";
  import vSelect from "vue-select";
  import OpenIndicator from "@/assets/svg/heroicons/selector.svg";
  import userImage from "@/components/general/user-image.vue";
  import { icons } from "@/assets/svg";
  import { createPopper } from "@popperjs/core";

  // Set the default Open Indicator for all v-select components
  vSelect.props.components.default = () => ({ OpenIndicator });

  export default {
    name: "FormSelect",
    components: {
      VSelect: vSelect,
      userImage,
      IconPlus: icons.PlusIcon,
      ...icons,
    },
    props: {
      name: {
        type: String,
        default: "",
      },
      value: {
        type: [String, Number, Array, Object],
        default: "",
      },
      placeholder: {
        type: String,
        default: "",
      },
      rules: {
        type: [String, Object],
        default: "",
      },
      options: {
        type: Array,
        default: () => [],
      },
      label: {
        type: String,
        default: "",
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      taggable: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: true,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
      labelClass: {
        type: String,
        default: "text-12",
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      showError: {
        type: Boolean,
        default: true,
      },
      canAddNew: {
        type: Boolean,
        default: false,
      },
      reduce: {
        type: Function,
        default: (o) => o,
      },
      createOption: {
        type: Function,
        default: undefined,
      },
      className: {
        type: String,
        default: "",
      },
      fakeRequired: {
        type: Boolean,
        default: false,
      },
      placement: {
        type: String,
        default: "bottom",
      },
    },
    emits: ["update:value", "blur", "focus", "new", "search"],
    methods: {
      withPopper(dropdownList, component, { width }) {
        /**
         * We need to explicitly define the dropdown width since
         * it is usually inherited from the parent with CSS.
         */
        dropdownList.style.width = width;

        /**
         * Here we position the dropdownList relative to the $refs.toggle Element.
         *
         * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
         * the dropdownList overlap by 1 pixel.
         *
         * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
         * wrapper so that we can set some styles for when the dropdown is placed
         * above.
         */
        const popper = createPopper(component.$refs.toggle, dropdownList, {
          placement: this.placement,
          strategy: "fixed",
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -1],
              },
            },
            {
              name: "toggleClass",
              enabled: true,
              phase: "write",
              fn({ state }) {
                component.$el.classList.toggle("drop-up", state.placement === "top");
              },
            },
          ],
        });

        // Store popper instance in a property so it can be accessed from other methods
        this.popperInstance = popper;

        /**
         * To prevent memory leaks Popper needs to be destroyed.
         * If you return function, it will be called just before dropdown is removed from DOM.
         */
        return () => popper.destroy();
      },
      updatePopperPosition() {
        if (this.popperInstance) {
          this.popperInstance.update();
        }
      },
    },
    setup(props, { emit, slots }) {
      const isFocussed = ref(false);
      const hovering = ref(false);
      const prefixWidth = ref("20px");
      const suffixWidth = ref("20px");
      const prefixEl = ref(null);
      const suffixEl = ref(null);
      const input = ref(null);
      const dropdownShouldOpen = ref(false);
      const selectContainer = ref(null);
      const instance = getCurrentInstance();

      // Use vee-validate's useField for validation
      const validateAs = computed(() => {
        if (props.label) {
          return props.label;
        }
        if (props.placeholder) {
          return props.placeholder;
        }
        if (props.name) {
          return props.name;
        }
        return "Select field";
      });

      const {
        value: localValue,
        errorMessage,
        meta,
        handleBlur: fieldBlur,
        setValue,
        setTouched,
      } = useField(props.name || validateAs.value, props.rules, {
        initialValue: props.value,
        validateOnValueUpdate: true,
      });

      // Only show errors for touched fields
      const shouldShowError = computed(() => {
        return errorMessage.value && meta.touched;
      });

      const hasPrefix = computed(() => !!prefixEl.value);
      const hasSuffix = computed(() => !!suffixEl.value);

      const hasRequired = computed(() => {
        if (typeof props.rules === "object") {
          return !!props.rules.required;
        }
        return false;
      });

      const enabledOptions = (option) => {
        return !option.hasOwnProperty("disabled") || (option.hasOwnProperty("disabled") && !option.disabled);
      };

      const setPreSuffixWidth = () => {
        if (hasPrefix.value && prefixEl.value) {
          prefixWidth.value = `${prefixEl.value.offsetWidth + 16}px`;
        }
        if (hasSuffix.value && suffixEl.value) {
          suffixWidth.value = `${suffixEl.value.offsetWidth + 16}px`;
        }
      };

      const handleInput = (e) => {
        if (!props.multiple || (props.multiple && e.length === props.options?.length)) {
          dropdownShouldOpen.value = false;
        } else {
          // For multiple select, update Popper position immediately using RAF
          requestAnimationFrame(() => {
            instance?.proxy?.updatePopperPosition();
          });
        }
        // in the next tick, emit the input event and set the value
        setTimeout(() => {
          emit("input", e);
          setTouched(true);
        }, 0);
      };

      const handleBlur = (e) => {
        isFocussed.value = false;
        fieldBlur();
        emit("blur", e);
      };

      const handleFocus = (e) => {
        isFocussed.value = true;
        emit("focus", e);
      };

      const addNew = () => {
        if (input.value) {
          input.value.open = false;
        }
        emit("new", true);
      };

      // Handle clicks outside the component using mousedown instead of click
      const handleClickOutside = (event) => {
        const menu = document.querySelector(".vs__dropdown-menu");
        if (selectContainer.value && menu && !selectContainer.value.contains(event.target) && !menu.contains(event.target)) {
          dropdownShouldOpen.value = false;
        }
      };

      // Toggle dropdown when clicking on the select
      const toggleDropdown = (event) => {
        // If the select is disabled, don't toggle the dropdown
        if (props.disabled) return;

        const deselect = event.target.closest(".vs__deselect");
        if (deselect) return;
        // Prevent this event from reaching the document listener immediately
        event.stopPropagation();

        // Toggle dropdown state
        dropdownShouldOpen.value = !dropdownShouldOpen.value;

        // If we're opening the dropdown, we need to add a delay before
        // the document listener can close it (prevents immediate closing)
        if (dropdownShouldOpen.value) {
          // Use setTimeout to allow this click event to finish
          setTimeout(() => {}, 0);
        }
      };

      // Apply rounded corners to the dropdown toggle and menu when the dropdown is open
      watch(dropdownShouldOpen, (newValue) => {
        const dropdownToggle = selectContainer.value.querySelector(".vs__dropdown-toggle");
        if (!newValue) {
          dropdownToggle.classList.remove("!rounded-b-none", "!rounded-t-none", "!rounded-b-lg", "!rounded-t-lg");
          return;
        }
        // Wait for the dropdown to be open
        setTimeout(() => {
          const menu = document.querySelector(".vs__dropdown-menu");

          if (menu.dataset.popperPlacement === "top") {
            dropdownToggle.classList.add("!rounded-t-none", "!rounded-b-lg");
            menu.classList.add("!rounded-b-none", "!rounded-t-lg");
          } else {
            dropdownToggle.classList.add("!rounded-b-none", "!rounded-t-lg");
            menu.classList.add("!rounded-b-lg", "!rounded-t-none");
          }
        }, 0);
      });

      // This watches for external changes to value
      watch(
        () => props.value,
        (newValue) => {
          if (newValue !== localValue.value) {
            setValue(newValue);
          }
        },
        { immediate: true },
      );

      // This emits changes from the input to the parent
      watch(localValue, (newValue) => {
        emit("update:value", newValue);
      });

      onMounted(() => {
        setPreSuffixWidth();
        document.addEventListener("click", handleClickOutside, true);
      });

      onUpdated(() => {
        setPreSuffixWidth();
      });

      onUnmounted(() => {
        document.removeEventListener("click", handleClickOutside, true);
      });

      return {
        localValue,
        errorMessage,
        meta,
        isFocussed,
        hovering,
        prefixWidth,
        suffixWidth,
        prefixEl,
        suffixEl,
        input,
        validateAs,
        hasPrefix,
        hasSuffix,
        hasRequired,
        handleBlur,
        handleInput,
        handleFocus,
        enabledOptions,
        addNew,
        dropdownShouldOpen,
        selectContainer,
        toggleDropdown,
        shouldShowError,
      };
    },
  };
</script>
